import { Box, FormControl, InputLabel, ListItemText, MenuItem, Select, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import { CountryFlag } from '@tix-ticketing/ui-icons/country-flag';
import { ChevronDownIcon } from '@tix-ticketing/ui-icons/outline';
import { AsYouType, getCountries, getCountryCallingCode } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';
import { useEffect, useId, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { GetCountryNameFromCode } from 'lib/helpers/string-helpers';
type PhoneInputProps = {
  /** Full mobile number as stored on the attendee (e.g. "+3548201234"). */
  value: string;
  /** Called with the recomposed full number whenever the prefix or number changes. */
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Default region used when the value carries no recognizable prefix. */
  defaultCountry: CountryCode;
  label: React.ReactNode;
  placeholder?: string;
  error?: boolean;
  helperText?: React.ReactNode;
  countryLabel: string;
  /** Emits the selected region whenever the country dropdown changes. */
  onCountryChange?: (country: CountryCode) => void;
};

/**
 * Detects the region of a stored mobile string. Falls back to `defaultCountry`
 * when the value has no prefix `AsYouType` can resolve (e.g. a partial number).
 */
function detectCountry(value: string, defaultCountry: CountryCode): CountryCode {
  const formatter = new AsYouType();
  formatter.input(value ?? '');
  const country = formatter.getCountry();
  if (value?.trim().startsWith('+') && country) {
    return country;
  }
  return defaultCountry;
}

/**
 * Recovers the national part by stripping the selected country's calling code.
 * The value is always composed here as "+<callingCode><national>", so removing
 * the known calling code is the exact inverse — unlike `AsYouType`, it stays
 * correct for partial numbers whose country it cannot yet resolve.
 */
function nationalPart(value: string, country: CountryCode): string {
  const raw = value ?? '';
  if (!raw.trim().startsWith('+')) return raw.replace(/\D/g, '');
  const digits = raw.replace(/\D/g, '');
  const callingCode = getCountryCallingCode(country);
  return digits.startsWith(callingCode) ? digits.slice(callingCode.length) : digits;
}

/**
 * Phone field with a country dial-code prefix dropdown. The composed value
 * ("+<callingCode><national>") is stored as a single string by the parent form.
 */
export const PhoneInput = ({
  value,
  onChange,
  onBlur,
  defaultCountry,
  label,
  placeholder,
  error,
  helperText,
  countryLabel,
  onCountryChange
}: PhoneInputProps) => {
  const intl = useIntl();
  const labelId = useId();
  const theme = useTheme();
  // Only genuinely narrow screens need the country code on its own line —
  // the row still fits comfortably down to ~360px of content width.
  const stackVertically = useMediaQuery(theme.breakpoints.down(360));
  const parsedCountry = detectCountry(value, defaultCountry);

  // The composed value drops its prefix once the number is cleared, so the
  // selected country is kept locally to survive an empty number. When the
  // stored value carries its own prefix (an external reset or applied
  // suggestion) the parsed country takes over.
  const [country, setCountry] = useState<CountryCode>(parsedCountry);
  useEffect(() => {
    if (value?.trim().startsWith('+')) {
      setCountry(parsedCountry);
    }
  }, [parsedCountry, value]);

  // National part is derived from the selected country, not from the prefix the
  // value happens to carry, so each keystroke maps back to a single digit.
  const national = nationalPart(value, country);
  const countryOptions = useMemo(() => {
    return getCountries().map(code => ({
      code,
      name: GetCountryNameFromCode(code, intl.locale) ?? code
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [intl.locale]);
  const compose = (nextCountry: CountryCode, nextNational: string) => {
    const digits = nextNational.replace(/\D/g, '');
    if (!digits) {
      onChange('');
      return;
    }
    onChange(`+${getCountryCallingCode(nextCountry)}${digits}`);
  };
  const handleCountryChange = (event: SelectChangeEvent) => {
    const nextCountry = event.target.value as CountryCode;
    setCountry(nextCountry);
    onCountryChange?.(nextCountry);
    compose(nextCountry, national);
  };
  const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    compose(country, event.target.value);
  };
  return <Stack direction={stackVertically ? 'column' : 'row'} spacing={2} alignItems={stackVertically ? 'stretch' : 'flex-start'} sx={{
    width: '100%'
  }}>
      <FormControl sx={{
      width: stackVertically ? '100%' : 120,
      flexShrink: 0
    }}>
        <InputLabel id={labelId}>{countryLabel}</InputLabel>
        <Select labelId={labelId} value={country} onChange={handleCountryChange} label={countryLabel} IconComponent={ChevronDownIcon} renderValue={selected => <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
              <CountryFlag code={selected} />
              <Typography variant="body1" sx={{
          whiteSpace: 'nowrap'
        }}>
                +{getCountryCallingCode(selected as CountryCode)}
              </Typography>
            </Box>} MenuProps={{
        sx: {
          maxHeight: 560
        }
      }}>
          {countryOptions.map(option => <MenuItem key={option.code} value={option.code}>
              <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
                <CountryFlag code={option.code} />
                <ListItemText primary={option.name} sx={{
              m: 0
            }} />
              </Box>
            </MenuItem>)}
        </Select>
      </FormControl>
      <TextField fullWidth label={label} placeholder={placeholder} value={national} onChange={handleNumberChange} onBlur={onBlur} error={error} helperText={helperText} inputMode="tel" slotProps={{
      formHelperText: {
        component: 'div'
      }
    }} />
    </Stack>;
};